import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDistanceToNow } from "date-fns";
import { ListSkeleton } from "@/components/Skeletons";
import { Search, Plus, ArrowUpRight } from "lucide-react";

const STATUS_TONE = {
  open: "bg-primary/10 text-primary border-primary/30",
  in_progress: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  waiting_staff: "bg-primary/10 text-primary border-primary/30",
  waiting_customer: "bg-accent/10 text-accent border-accent/30",
  solved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
  reopened: "bg-accent/10 text-accent border-accent/30",
};

const PRIORITY_TONE = {
  low: "text-muted-foreground",
  normal: "text-foreground",
  high: "text-amber-500",
  urgent: "text-accent",
  critical: "text-destructive",
};

export default function Tickets() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [scope, setScope] = useState(params.get("scope") || (user?.role === "customer" ? "mine" : "mine"));
  const [status, setStatus] = useState(params.get("status") || "all");
  const [q, setQ] = useState("");
  const [tickets, setTickets] = useState(null);

  useEffect(() => {
    const query = new URLSearchParams({ scope, limit: "60" });
    if (status !== "all") query.set("status", status);
    if (q) query.set("q", q);
    setTickets(null);
    api.get(`/tickets?${query}`).then((r) => setTickets(r.data)).catch(() => setTickets([]));
    setParams({ scope, ...(status !== "all" && { status }) });
  }, [scope, status, q, setParams]);

  const isStaff = user?.role === "agent" || user?.role === "admin";

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-primary mb-2">// Tickets</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Support inbox</h1>
        </div>
        <Button data-testid="tickets-new-btn" asChild className="gap-2"><Link to="/tickets/new"><Plus className="h-4 w-4" /> New ticket</Link></Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        {isStaff && (
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList>
              <TabsTrigger value="mine" data-testid="tab-mine">My tickets</TabsTrigger>
              <TabsTrigger value="assigned" data-testid="tab-assigned">Assigned</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="waiting_customer">Waiting customer</SelectItem>
            <SelectItem value="waiting_staff">Waiting staff</SelectItem>
            <SelectItem value="solved">Solved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="tickets-search" placeholder="Search subject or number" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
      </div>

      {tickets === null ? (
        <ListSkeleton rows={6} />
      ) : tickets.length === 0 ? (
        <div className="p-16 text-center rounded-lg border border-dashed border-border/60 text-sm text-muted-foreground">
          No tickets match your filters.
        </div>
      ) : (
        <div className="rounded-lg border border-border/60 overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/40">
              <tr>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">ID</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Subject</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Priority</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hidden lg:table-cell">Updated</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.ticket_id} className="border-b border-border/40 hover:bg-secondary/40 transition-colors group">
                  <td className="px-4 py-3 font-mono text-xs">{t.ticket_number}</td>
                  <td className="px-4 py-3 max-w-[420px]">
                    <Link to={`/tickets/${t.ticket_id}`} className="hover:text-primary" data-testid={`ticket-row-${t.ticket_number}`}>
                      <div className="truncate font-medium">{t.subject}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">{t.category}</td>
                  <td className={`px-4 py-3 text-xs font-mono uppercase tracking-widest ${PRIORITY_TONE[t.priority] || ""}`}>{t.priority}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded border ${STATUS_TONE[t.status] || ""}`}>{t.status.replace(/_/g," ")}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">{formatDistanceToNow(new Date(t.last_activity_at), { addSuffix: true })}</td>
                  <td className="px-4 py-3"><Link to={`/tickets/${t.ticket_id}`}><ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 group-hover:text-primary" /></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
